import type { ServerProvider } from "@cmmd-forge/contracts";

import { describeProviderAvailability } from "../../cmmd/providerIneligibilityCopy";
import type {
  PickerRailProvider,
  ShellProviderAvailability,
} from "../../cmmd/providerAvailabilityJoin";
import type { SidebarProviderEntry } from "./ModelPickerSidebar";

/**
 * One rail row, from the Shell's joined availability plus whatever snapshot the
 * Environment published.
 *
 * The availability is the only source of a withheld reason here. This module
 * used to derive its own: a provider missing from the payload fell through to
 * an "unlisted" branch, and the four reasons a person needs to tell apart were
 * reconstructed from an absence. That is the defect
 * `docs/adr/withheld-providers-carry-their-reason.md` closes, so nothing below
 * looks at whether a snapshot exists in order to decide why one is withheld.
 */

/**
 * A provider that is usable and has a snapshot speaking for it.
 *
 * No message means the snapshot had nothing to say: leave it unsaid rather than
 * borrow the roadmap sentence.
 */
function usableProviderEntry(
  provider: PickerRailProvider,
  live: ServerProvider,
): SidebarProviderEntry {
  // Only an EXPLICIT "unauthenticated" counts. "unknown" must not flag: a
  // control-plane-served Codex reports a non-"authenticated" status while
  // working perfectly (forge#2002), and steering people away from the one
  // provider that actually works is a worse failure than the silence this
  // replaces.
  const requiresSignIn = live.auth.status === "unauthenticated";
  return {
    provider,
    available: live.enabled,
    status: live.status,
    ...(live.message ? { statusMessage: live.message } : {}),
    ...(requiresSignIn ? { requiresSignIn } : {}),
  };
}

export function buildSidebarEntry(input: {
  readonly provider: PickerRailProvider;
  readonly availability: ShellProviderAvailability;
  readonly snapshot: ServerProvider | undefined;
}): SidebarProviderEntry {
  const { availability, provider, snapshot } = input;
  if (availability.state === "usable") {
    return snapshot
      ? usableProviderEntry(provider, snapshot)
      : { provider, available: true, status: "unknown" };
  }
  const sentence = describeProviderAvailability(provider, availability);
  // No verdict has arrived yet, so the row stays selectable: switching off the
  // control whose job is "pick a provider that works" because we do not yet
  // know which one does is the failure #2052 fixed one level up. It still says
  // what it is waiting for, because the model rows behind it are greyed.
  if (availability.state === "unresolved" && availability.cause === "loading") {
    return {
      provider,
      available: true,
      status: "unknown",
      ...(sentence ? { sentence } : {}),
    };
  }
  if (sentence === null) return { provider, available: true, status: "unknown" };

  // A Withheld Provider stays visible and openable so its models render greyed
  // and a person can see what they would get. A roadmap row is the exception:
  // nothing publishes it, so opening it would show an empty list.
  const comingSoon = availability.state === "withheld" && availability.reason === "unbuilt";
  const inspectable = availability.state === "withheld" && !comingSoon;
  return {
    provider,
    available: false,
    status: availability.state === "withheld" ? "disabled" : "unknown",
    ...(inspectable ? { inspectable } : {}),
    ...(comingSoon ? { comingSoon } : {}),
    sentence,
  };
}
